/**
 * 139 — how much vertical space the shell's own furniture occupies, summed from contributions that
 * each report their own measured height.
 *
 * 🔴 This replaces a hardcoded `132px` that lived in fourteen screens and was wrong in four
 * distinguishable ways (research.md R1): it omitted the global notices, omitted the install offer,
 * was applied on routes that render no chrome at all, and — the one that caused a visible defect —
 * assumed the notification bell renders. `ShellNotificationBell` returns `null` unless the visitor
 * is signed in (`main.tsx:47`), so on any `showNav` route where they are not, the real chrome is
 * 80px rather than 132px. `/impersonate` was not a special case; it was simply the first place
 * anyone measured.
 *
 * Deliberately pure and DOM-free. jsdom reports every element's `offsetHeight` as 0 and implements
 * no `ResizeObserver`, so logic that lived in the DOM could not be tested at all, and a test
 * asserting the published pixel figure would pass against almost any implementation (R6).
 */
export const SHELL_CHROME_VAR = '--shell-chrome-h';

export interface ChromeContributions {
  /** Measured height of the global notice slot — impersonation banner, update notice. */
  noticesPx: number;
  /** Measured height of the notification bell row. Zero whenever the bell does not render. */
  bellPx: number;
  /** The shell's own reserve for the fixed bottom navigation. Zero where the nav is hidden. */
  navReservePx: number;
  /** Measured height of the install offer, which overlays the bottom edge (138). */
  offerPx: number;
}

/**
 * A contribution that is absent, `undefined`, negative or non-finite counts as **zero**. Absent
 * means "this chrome is not on screen", which is the correction above. Negative and non-finite are
 * defensive: one bad measurement must not reach `calc(100dvh - …)`, where it would make screens
 * taller than the viewport or invalidate the declaration outright.
 */
export function sumChromeHeight(parts: Partial<ChromeContributions>): number {
  const clamp = (value: number | undefined): number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

  // Rounded UP, never down: under-reserving by a fraction of a pixel leaves a sliver of scroll on
  // a screen that should have none, which is the defect this feature removes. Over-reserving by
  // less than a pixel is invisible.
  return Math.ceil(
    clamp(parts.noticesPx) + clamp(parts.bellPx) + clamp(parts.navReservePx) + clamp(parts.offerPx),
  );
}
